// src/games/Sudoku/rules.ts
// Regras puras do sudoku (contrato P0). A célula tem o MESMO formato que o app
// grava no cache, para o progresso abrir no app e no portal.
import type { iGameSudoku } from '../../interfaces/sudoku';
import type { SudokuShape } from './shape';

export interface SudokuCell {
  solution: string;
  /** Linha (0-based). */
  x: number;
  /** Coluna (0-based). */
  y: number;
  isHint: boolean;
  borderTop: boolean;
  borderRight: boolean;
  value: string;
  /** Contrato P0: casa revelada não pontua; ausente = não revelada. */
  revelada?: true;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function mapCells(cells: SudokuCell[][], fn: (cell: SudokuCell) => SudokuCell): SudokuCell[][] {
  return cells.map((row) => row.map((cell) => fn(cell)));
}

function updateCell(cells: SudokuCell[][], x: number, y: number, fn: (cell: SudokuCell) => SudokuCell): SudokuCell[][] {
  return cells.map((row, i) => (i !== x ? row : row.map((cell, j) => (j === y ? fn(cell) : cell))));
}

export function buildSudokuCells(game: iGameSudoku, shape: SudokuShape): SudokuCell[][] {
  const grid = game['crossword-compiler']['rectangular-puzzle'].sudoku.grid;
  const height = parseInt(grid.attributes.height, 10);
  const width = parseInt(grid.attributes.width, 10);
  const cells: SudokuCell[][] = Array.from({ length: height }, () => []);
  grid.cell.forEach(({ attributes }) => {
    const x = parseInt(attributes.x, 10);
    const y = parseInt(attributes.y, 10);
    if (!(x >= 1 && x <= height && y >= 1 && y <= width)) return;
    cells[x - 1][y - 1] = {
      solution: String(attributes.solution),
      x: x - 1,
      y: y - 1,
      isHint: Boolean(attributes.hint),
      borderTop: x % shape.boxH === 0 && x < height,
      borderRight: y % shape.boxW === 0 && y < width,
      value: '',
    };
  });
  return cells;
}

function parseCache(cache: unknown): unknown {
  let parsed = cache;
  // legado do portal: JSON.stringify de uma string JSON
  for (let i = 0; i < 2 && typeof parsed === 'string'; i++) {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }
  return parsed;
}

/**
 * Contrato ("Cache antigo do sudoku"): só vale cache em matriz cujas células tenham
 * `solution` (formato do app/deste portal). O `{ value }` antigo do portal é inválido.
 */
export function isSudokuCacheValid(parsed: unknown): parsed is unknown[][] {
  if (!Array.isArray(parsed) || parsed.length === 0) return false;
  return parsed.every(
    (row) =>
      Array.isArray(row) &&
      row.every((cell) => isRecord(cell) && (typeof cell.solution === 'string' || typeof cell.solution === 'number'))
  );
}

/** Aplica o cache salvo sobre a grade do jogo; cache inválido → começa do zero. */
export function hydrateSudokuCells(base: SudokuCell[][], cache: unknown): SudokuCell[][] {
  const parsed = parseCache(cache);
  if (!isSudokuCacheValid(parsed)) return base.map((row) => row.map((cell) => ({ ...cell })));
  const rows: unknown[] = parsed;
  return base.map((row, x) =>
    row.map((cell, y): SudokuCell => {
      if (cell.isHint) return { ...cell };
      const savedRow = rows[x];
      const saved: unknown = Array.isArray(savedRow) ? savedRow[y] : undefined;
      if (!isRecord(saved)) return { ...cell };
      if (saved.revelada === true) return { ...cell, value: cell.solution, revelada: true };
      const value = typeof saved.value === 'string' || typeof saved.value === 'number' ? String(saved.value) : '';
      return { ...cell, value };
    })
  );
}

const isRight = (cell: SudokuCell) => !cell.isHint && cell.value !== '' && cell.value === cell.solution;

export function fillableCount(cells: SudokuCell[][]): number {
  return cells.reduce((acc, row) => acc + row.filter((cell) => !cell.isHint).length, 0);
}

/** Contrato: casas não-dadas com valor == solução E sem revelada. */
export function scoreSudoku(cells: SudokuCell[][]): number {
  return cells.reduce((acc, row) => acc + row.filter((cell) => isRight(cell) && !cell.revelada).length, 0);
}

/** Acertos mostrados no fim (inclui as reveladas). */
export function countSudokuCorrect(cells: SudokuCell[][]): number {
  return cells.reduce((acc, row) => acc + row.filter(isRight).length, 0);
}

export function isSudokuFilled(cells: SudokuCell[][]): boolean {
  return cells.length > 0 && cells.every((row) => row.every((cell) => cell.isHint || cell.value !== ''));
}

/** Casa editável/revelável: existe, não é dada e não foi revelada. */
export function canRevealSudoku(cells: SudokuCell[][], x: number, y: number): boolean {
  const cell = cells[x]?.[y];
  return !!cell && !cell.isHint && !cell.revelada;
}

export function setSudokuValue(cells: SudokuCell[][], x: number, y: number, value: string): SudokuCell[][] {
  if (!canRevealSudoku(cells, x, y)) return cells;
  return updateCell(cells, x, y, (cell) => ({ ...cell, value }));
}

export function revealSudokuCell(cells: SudokuCell[][], x: number, y: number): SudokuCell[][] {
  if (!canRevealSudoku(cells, x, y)) return cells;
  return updateCell(cells, x, y, (cell) => ({ ...cell, value: cell.solution, revelada: true }));
}

/** "Revelar resposta": todas as casas não-dadas viram reveladas (pontuação 0). */
export function revealAllSudoku(cells: SudokuCell[][]): SudokuCell[][] {
  return mapCells(cells, (cell) => (cell.isHint || cell.revelada ? cell : { ...cell, value: cell.solution, revelada: true }));
}

/** "Limpar jogo": apaga o digitado; dadas e reveladas continuam. */
export function clearSudoku(cells: SudokuCell[][]): SudokuCell[][] {
  return mapCells(cells, (cell) => (cell.isHint || cell.revelada ? cell : { ...cell, value: '' }));
}

/** Cache para `POST sudoku_progresso` (matriz, nunca string). */
export function toSudokuCache(cells: SudokuCell[][]): SudokuCell[][] {
  return mapCells(cells, (cell) => ({ ...cell }));
}
